/* eslint-disable react/prop-types */
import React from 'react'
import ReactList from 'react-list'
import styled, { css } from 'styled-components'
import {
  Modal as BaseModal,
  ModalHeader,
  ModalBody as BaseModalBody,
  ModalFooter,
} from 'reactstrap'

import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import moment from '../../../lib/moment'
import { createTable } from '../../../lib/components/Table'
import Tooltip, { withTooltip } from '../../../lib/components/Tooltip'
import Button from '../../../lib/components/Button'
import BaseTypography from '../../../lib/typography'

import bindActionCreators from '../../../lib/utils/bindActionCreators'

import { ppsAction } from '../../../actions'
import { ppsSelector } from '../../../selectors'

import { momentFormatter, financialFormatter } from '../../../lib/utils'

import ImportListTable from '../../../components/project-profit-sheet/ImportListTable'

const Typography = {
  ...BaseTypography,
  Status: styled(BaseTypography.Status)`
    width: 95%;
    height: 19px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
}

const Modal = styled(BaseModal)`
  .modal-content {
    height: 80vh;
    position: relative;
  }

  @media (min-width: 0) {
    max-width: 90%;
  }
`

const ModalBody = styled(BaseModalBody)`
  height: calc(100% - 57px);
  padding: 12px 16px 0;
  position: relative;
`

// prettier-ignore
const DetailHeader = styled.div`
  height: 63px;
  padding: 6px 12px 0;

  display: grid;
  justify-items: start;
  align-items: center;
  grid-template-columns:
    calc(100% * 1 / 5)
    calc(100% * 1 / 5)
    calc(100% * 1 / 5)
    calc(100% * 1 / 5)
    calc(100% * 1 / 5);

  background-color: #fff;
  border-top: 1px solid #404040;
  border-left: 1px solid #404040;
  border-right: 1px solid #404040;

  ${Typography.Caption} {
    display: block;
    color: #333333;
    font-size: 13px;
    line-height: auto;
  }
`

const DetailField = styled.div`
  width: 100%;
`

const DetailFieldTooltip = styled(DetailField)`
  ${withTooltip}
`

// prettier-ignore
const BaseTable = createTable(css`
  grid-template-columns:
    100px
    100px
    300px
    180px
    90px
    90px
    100px
    100px
    minmax(20%, 1fr)
    110px;
`)

const Table = {
  ...BaseTable,
  RowCell: styled(BaseTable.RowCell)`
    padding: 0 12px;
  `,
  RowCellTooltip: styled(BaseTable.RowCell)`
    ${withTooltip}

    ${Tooltip} {
      top: 80%;
    }

    &:hover ${Tooltip} {
      transition-delay: 1s;
    }
  `,
}

const Layout = {
  ScrollWrapper: styled.div`
    height: calc(100% - 105px);
    padding-bottom: 16px;

    overflow-y: scroll;
    overflow-x: hidden;
  `,
  Truncate: styled.div`
    max-height: 100%;
    overflow: hidden;

    // ! non-standard
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `,
}

/**
 * @typedef {object} Props
 * @property {Array} data
 * @property {object} details
 * @property {boolean} canDelete
 * @property {typeof ppsAction.fetchImportDetail} getDetail
 * @property {typeof ppsAction.deleteImportTransactions} deleteImport
 * @property {typeof ppsAction.resetImportDetail} resetImportDetail
 * @property {typeof ppsAction.fetchProjectsData} fetchProjectsData
 */

/**
 * @typedef {object} State
 * @property {boolean} modal_open
 */

/** @extends {React.Component<Props, State>} */
class ImportTableContainer extends React.Component {
  state = {
    modal_open: false,
    modal_confirm: false,
  }

  toggleModal = () => {
    this.setState(state => ({
      modal_open: !state.modal_open,
    }))
  }

  toggleConfirm = () => {
    this.setState(state => ({
      modal_confirm: !state.modal_confirm,
    }))
  }

  /** @type {(id: string) => () => Promise<void>} */
  viewDetail = id => async () => {
    const access_token = localStorage.getItem('access_token')

    await this.props.getDetail(id, access_token)
    this.toggleModal()
  }

  filterData = () => {
    const { data, search } = this.props
    return data.filter(d => d.imported_by.toLowerCase().indexOf(search.toLowerCase()) > -1)
  }

  deleteConfirm = async () => {
    const { deleteImport, details, resetImportDetail, fetchProjectsData } = this.props
    const access_token = localStorage.getItem('access_token')

    this.toggleModal()
    this.toggleConfirm()

    await deleteImport(details.project_profit_accounting_id, access_token)
    await fetchProjectsData(access_token)

    resetImportDetail()
  }

  renderItem = (idx, key) => {
    const { details } = this.props
    return (
      <Table.Row key={ key }>
        <Table.RowCellTooltip>
          <Table.Ellipsis>{details.transactions[idx].project_code}</Table.Ellipsis>
          <Tooltip>{details.transactions[idx].project_code}</Tooltip>
        </Table.RowCellTooltip>
        <Table.RowCell>{details.transactions[idx].sub_project_code}</Table.RowCell>
        <Table.RowCell>{details.transactions[idx].project_name}</Table.RowCell>
        <Table.RowCell>{details.transactions[idx].client_name}</Table.RowCell>
        <Table.RowCell>{details.transactions[idx].account_code}</Table.RowCell>
        <Table.RowCell>{details.transactions[idx].type}</Table.RowCell>
        <Table.RowCell>{details.transactions[idx].product_name}</Table.RowCell>
        <Table.RowCell>{moment(details.transactions[idx].date).format('DD/MM/YYYY')}</Table.RowCell>
        <Table.RowCell>
          <Layout.Truncate>{details.transactions[idx].description}</Layout.Truncate>
        </Table.RowCell>
        <Table.RowCell currency>
          {financialFormatter(details.transactions[idx].amount)}
        </Table.RowCell>
      </Table.Row>
    )
  }

  render = () => {
    const { details, sort, handleClickSort, canDelete } = this.props
    const { modal_open, modal_confirm } = this.state
    return (
      <React.Fragment>
        <Modal isOpen={ modal_open } toggle={ this.toggleModal }>
          <ModalHeader toggle={ this.toggleModal }>
            <Typography.ModalHeader>Detailed Imported Cost</Typography.ModalHeader>
          </ModalHeader>
          <ModalBody>
            {canDelete && (
              <Button color="danger" style={ { marginBottom: 12 } } onClick={ this.toggleConfirm }>
                Delete Records
              </Button>
            )}
            <DetailHeader>
              <DetailField>
                <Typography.Caption>Import Date</Typography.Caption>
                <Typography.Status>{momentFormatter('DD/MM/YYYY')(details.date)}</Typography.Status>
              </DetailField>
              <DetailField>
                <Typography.Caption>Transaction Amount</Typography.Caption>
                <Typography.Status>{details.transaction_amount}</Typography.Status>
              </DetailField>
              <DetailField>
                <Typography.Caption>Projects Amount</Typography.Caption>
                <Typography.Status>{details.project_amount}</Typography.Status>
              </DetailField>
              <DetailField>
                <Typography.Caption>Imported By</Typography.Caption>
                <Typography.Status>{details.imported_by}</Typography.Status>
              </DetailField>
              <DetailFieldTooltip>
                <Typography.Caption>Remark</Typography.Caption>
                <Typography.Status>{details.remark}</Typography.Status>
                <Tooltip style={ { maxWidth: 350 } }>{details.remark}</Tooltip>
              </DetailFieldTooltip>
            </DetailHeader>
            <Layout.ScrollWrapper>
              <Table.Head>
                <Table.HeadCell>Proj. Code</Table.HeadCell>
                <Table.HeadCell>Sub Code</Table.HeadCell>
                <Table.HeadCell>Project Name</Table.HeadCell>
                <Table.HeadCell>Client</Table.HeadCell>
                <Table.HeadCell>Code</Table.HeadCell>
                <Table.HeadCell>Type</Table.HeadCell>
                <Table.HeadCell>Sale Group</Table.HeadCell>
                <Table.HeadCell>Date</Table.HeadCell>
                <Table.HeadCell>Description</Table.HeadCell>
                <Table.HeadCell>Amount</Table.HeadCell>
              </Table.Head>
              <ReactList
                type="uniform"
                itemRenderer={ this.renderItem }
                length={ details.transactions.length }
              />
            </Layout.ScrollWrapper>
          </ModalBody>
        </Modal>
        <BaseModal isOpen={ modal_confirm } toggle={ this.toggleConfirm }>
          <ModalHeader style={ { padding: '12px 16px' } }>
            <Typography.ModalHeader>Confirm Delete Records</Typography.ModalHeader>
          </ModalHeader>
          <BaseModalBody>
            This action cannot be undone.
            <br />
            Are you sure to delete these records?
          </BaseModalBody>
          <ModalFooter style={ { padding: '12px 16px' } }>
            <Button outline color="secondary" onClick={ this.toggleConfirm }>
              No
            </Button>
            <Button color="danger" onClick={ this.deleteConfirm }>
              Yes, Delete
            </Button>
          </ModalFooter>
        </BaseModal>
        <ImportListTable
          colClick={ col => () => handleClickSort(col) }
          sort={ sort }
          data={ this.filterData() }
          rowClick={ this.viewDetail }
        />
      </React.Fragment>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  data: ppsSelector.accountingCosts,
  details: ppsSelector.importDetail,
  canDelete: ppsSelector.canUseAccountantActions,
})

const mapDispatchToProps = bindActionCreators({
  getDetail: ppsAction.fetchImportDetail,
  deleteImport: ppsAction.deleteImportTransactions,
  resetImportDetail: ppsAction.resetImportDetail,
  fetchProjectsData: ppsAction.fetchProjectsData,
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ImportTableContainer)



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/containers/ImportTableContainer.js